//
//
//
//
//
//

import { v4 as uuidv4 } from "uuid";
import { lineString, polygon as polygonFunc } from "@turf/helpers";
import centerOfMass from "@turf/center-of-mass"
import bbox from "@turf/bbox"
import area from "@turf/area"
import bboxPolygon from "@turf/bbox-polygon"
import { addServerPath } from "@/plugins/add-server-path";
import { STORAGE_BACKGROUND_PATH, BUILD_GENERAL_STORAGE_IMAGE_PATH } from "@/constants/ui.js";
import { MapSchemeEditStyles } from "@/utils/dto/map.js";
import { hexToRgb } from "@/utils/helpers.js";
import debounce from 'lodash/debounce';


export default {
  name: "MapView",
  provide() {
    return {
      source: this.source
    }
  },
  props: {
    initial: {
      type: Object
    },
    typeViewPlacemark: {
      type: String,
      required: false,
      default: 'circle'
    },
    zoom: {
      type: Boolean,
      default: false
    },
    styling: {
      type: Boolean,
      default: false
    },
    showScheme: {
      type: Boolean,
      default: false
    },
    schemeStyles: {
      type: [MapSchemeEditStyles, Object], //TODO
      default: () => null
    },
  },
  data() {
    return {
      id: null,
      instance: null,
      ready: false,
      loading: false,
      initialData: this.initial,
      geoCollection: null,
      pictoByObj: {},
      canvas: null,
      schemeData: {
        polygons: {
          polygons: []
        }
      }
    }
  },
  computed: {
    globalGeometry() {
      return Object.values(this.initialData).sort((a, b) => a?.object?.conditionIndex - b?.object?.conditionIndex).map(x => {
        x.uuid = uuidv4()

        switch (x.type) {
        case "polygon":
          const polygonGeometry = polygonFunc(x.geometry)
          const bboxPolygonGeometry = bbox(polygonGeometry)
          x['center-of-mass'] = centerOfMass(polygonGeometry).geometry.coordinates
          x['bounds'] = [
            [bboxPolygonGeometry[0], bboxPolygonGeometry[1]],
            [bboxPolygonGeometry[2], bboxPolygonGeometry[3]],
          ]
          break;
        case "polyline":
          const lineStringGeometry = lineString(x.geometry)
          const bboxLineStringGeometry = bbox(lineStringGeometry)
          x['center-of-mass'] = centerOfMass(lineStringGeometry).geometry.coordinates
          x['bounds'] = [
            [bboxLineStringGeometry[0], bboxLineStringGeometry[1]],
            [bboxLineStringGeometry[2], bboxLineStringGeometry[3]],
          ]
          break;
        case "circle":
          x['center-of-mass'] = x.geometry
          break;
        }

        return x
      })
    },
    STORAGE_BACKGROUND_PATH() {
      return STORAGE_BACKGROUND_PATH
    },
  },
  watch: {
    initial: {
      handler(newVal) {
        this.initialData = newVal
        this.updateGeodata()
      }
    },
    showScheme: {
      handler(newVal) {
        if (newVal) {
          this.instance.destroy()
          this.init()
        }
      },
    }
  },
  mounted() {
    this.ready = true
    this.id = uuidv4()
    this.$refs["map-frame"].setAttribute('id', this.id)

    // eslint-disable-next-line nuxt/no-env-in-hooks
    if (process.client) {
      window.addEventListener('ymap-init', this.readyMapHandler)

      if (window.mapObjectInit === undefined) {
        window.mapObjectInit = () => {
          window.dispatchEvent(new CustomEvent('ymap-init'))
        }
      }

      if (!window.ymaps) {
        const observer = new window.IntersectionObserver(([entry]) => {
          if (entry.isIntersecting && !this.loading && !window.addedYmaps) {
            this.loading = true
            const elem = document.createElement('script');
            elem.type = 'text/javascript';
            elem.src = '//api-maps.yandex.ru/2.1/?apikey=34a52aa6-9e5b-4c91-8246-ebe300263a6f&lang=ru_RU&onload=mapObjectInit';
            document.getElementsByTagName('body')[0].appendChild(elem);
            window.addedYmaps = true
          }
        }, {
          root: null,
          threshold: 0
        })

        observer.observe(this.$refs["map-frame"]);

      } else {
        this.init()
      }
    }

    window.addEventListener('resize', this.resizeHandler, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('ymap-init', this.readyMapHandler)
    window.removeEventListener('resize', this.resizeHandler)
  },
  methods: {
    updateGeodata() {
      if (this.instance) {
        this.instance.destroy()
        this.init()
      } else {
        setTimeout(() => {
          if (this.instance) {
            this.instance.destroy()
            this.init()
          }
        }, 0)
      }
    },
    async setBounds() {
      const bounds = Object.values(this.initialData)
        .map(item => {
          return item.bounds
        })
        .flat()

      this.instance.setBounds(ymaps.util.bounds.fromPoints(bounds), {
        checkZoomRange: true,
        zoomMargin: 35
      }).then(() => {
        const zoom = this.instance.getZoom()

        if (zoom > 17) {
          this.instance.setZoom(17)
        }
      })
    },
    async buildGeoObject(type = 'circle', coordinates = [], _uuid = "", object, styles) {
      return new Promise(async (resolve) => {
        const bgCollectionID = styles?.background?.collectionID
        const bgID = styles?.background?.bgID
        const bgColor = styles?.background?.color
        const bgOpacity = styles?.background?.opacity
        const borderColor = styles?.border?.color
        const borderLineRaw = styles?.border?.line
        const isBorderLine = borderLineRaw !== 'none'
        const borderLine = isBorderLine ? this.normalizeStrokeStyle(borderLineRaw) : null
        const borderLineWidth = styles?.border?.lineWidth
        const borderShadow = styles?.border?.shadow === 'has'
        const iconCollectionID = styles?.icon?.collectionID
        const iconBackgroundColor = styles?.icon?.backgroundColor
        const iconBorderColor = styles?.icon?.borderColor
        const iconShadow = styles?.icon?.shadow === 'has'
        const iconSize = styles?.icon?.size ?? 36
        const iconExistence = styles?.icon?.existence
        const nameDirection = styles?.name?.direction
        const nameFontSize = styles?.name?.fontSize
        const nameColor = styles?.name?.color
        const nameShow = styles?.name?.show
        const shadowOffset = iconSize / 20

        const circleOptions = {
        // preset: 'islands#circleDotIcon',
          iconLayout: 'default#image',
          iconImageHref: require('@/assets/svg/location.svg'),
          iconImageSize: [iconSize, iconSize],
          iconImageOffset: [-(iconSize / 2), -(iconSize / 1.2)],
          iconColor: '#328fe5',
          iconShadow: iconShadow,
          iconShadowLayout: 'default#image',
          iconShadowImageSize: [iconSize - shadowOffset, iconSize - shadowOffset],
          iconShadowImageOffset: [-(iconSize / 2.8), -(iconSize / 4.5)],
          zIndex: 400 + (object?.conditionIndex || 0),
        }
        const polylineOptions = {
          editorDrawingCursor: "crosshair",
          editorMinPoints: 2,
          fillColor: this.styling ? `rgba(${this.hexToRgb(bgColor ?? '#328fe5')}, ${bgOpacity / 100}))` : 'rgba(50, 143, 229, 0.2)',
          stroke: isBorderLine,
          strokeColor: borderColor ? borderColor : '#328FE6',
          strokeWidth: isBorderLine && borderLineWidth ? borderLineWidth : 2,
          strokeStyle: borderLine ? borderLine : 'solid',
          zIndex: 400 + (object?.conditionIndex || 0),
        }
        const polygonOptions = {
          editorDrawingCursor: "crosshair",
          editorMinPoints: 3,
          // fillMethod: 'stretch',
          fillColor: this.styling ? `rgba(${this.hexToRgb(bgColor ?? '#328fe5')}, ${bgOpacity / 100})` : 'rgba(50, 143, 229, 0.2)',
          stroke: isBorderLine,
          strokeColor: borderColor ?? '#328FE6',
          strokeWidth: isBorderLine && borderLineWidth ? borderLineWidth : 1,
          strokeStyle: borderLine ? borderLine : 'solid',
          zIndex: 400 + (object?.conditionIndex || 0),
        }
        const polygonShadowOptions = {
          strokeWidth: isBorderLine && borderLineWidth ? borderLineWidth + 10 : 0,
          strokeOpacity: 0.2,
          fillOpacity: 0,
          zIndex: 300 + (object?.conditionIndex || 0),
          strokeColor: borderColor ? borderColor + '20' : '#328FE620',
          strokeStyle: borderLine ? borderLine : 'solid',
        }
        const polylineShadowOptions = {
          strokeWidth: isBorderLine && borderLineWidth ? borderLineWidth + 10 : 0,
          strokeOpacity: 0.5,
          fillOpacity: 0,
          zIndex: 300 + (object?.conditionIndex || 0),
          strokeColor: borderColor ? borderColor + '20' : '#328FE620',
          strokeStyle: borderLine ? borderLine : 'solid',
        }

        const geoObjectUuid = _uuid
        let geoObject = null
        let shadowGeoObject = null
        let textGeoObject = null

        switch (type) {
        case "polygon":
          geoObject = new ymaps.Polygon(coordinates, {
            uuid: geoObjectUuid,
          }, polygonOptions);
          geoObject.properties.set('center-of-mass', centerOfMass(polygonFunc(coordinates)))

          if (borderShadow) {
            shadowGeoObject = new ymaps.Polygon(coordinates, {
              shadowUuid: geoObjectUuid,
            }, polygonShadowOptions);
          }

          if (object?.label) {
            textGeoObject = new ymaps.Placemark(centerOfMass(polygonFunc(coordinates)).geometry.coordinates, { label: object?.label }, {
              iconLayout: 'default#image',
              iconImageHref: '',
              iconImageSize: [0, 0],
              zIndex: 500 + (object?.conditionIndex || 0)
            })
            let imgUrl = '';

            if (object?.type?.label?.toLowerCase() === 'событие') {
              switch (iconExistence) {
              case 'own':
                imgUrl = iconCollectionID ? addServerPath(`/admin/icon_files/${iconCollectionID}/picto_black/`) : ''
                break;
              case 'type':
                imgUrl = object?.type?.icon ? addServerPath(`/admin/icon_files/${object?.type?.icon}/picto_black/`) : '';
                break;
              case 'photo':
                imgUrl = object?.event_icon ? addServerPath(`/admin/icon_files/${object?.event_icon}/picto_black/`) : '';
                break;
              }
            } else {
              switch (iconExistence) {
              case 'own':
                imgUrl = iconCollectionID ? addServerPath(`/admin/icon_files/${iconCollectionID}/picto_black/`) : ''
                break;
              case 'type':
                imgUrl = object?.type?.icon ? addServerPath(`/admin/icon_files/${object?.type?.icon}/picto_black/`) : iconCollectionID ? addServerPath(`/admin/icon_files/${iconCollectionID}/picto_black/`) : '';
                break;
              case 'photo':
                if (object?.icon) {
                  imgUrl = addServerPath(`/admin/icon_files/${object?.icon}/picto_black/`)
                } else if (object?.has_image) {
                  imgUrl = addServerPath(`/public/object_images_files/first_image/${object?.id}/?preview=1`)
                } else if (object?.type?.icon) {
                  imgUrl = addServerPath(`/admin/icon_files/${object?.type?.icon}/picto_black/`)
                } else if (iconCollectionID) {
                  imgUrl = addServerPath(`/admin/icon_files/${iconCollectionID}/picto_black/`)
                }
                break;
              }
            }

            if(imgUrl.length) {
              let customImage = new Image();
              customImage.crossOrigin = "Anonymous";
              customImage.onload = function () {

                let canvas = document.createElement('canvas');
                let size = iconSize;
                canvas.width = size;
                canvas.height = size;
                let ctx = canvas.getContext('2d');

                ctx.beginPath();
                ctx.arc(canvas.width / 2, canvas.height / 2, (canvas.width / 2) - 1, 0, 2 * Math.PI, false);
                if (iconBackgroundColor) {
                  ctx.fillStyle = iconBackgroundColor;
                  ctx.fill();
                }
                if (iconBorderColor) {
                  ctx.lineWidth = 2;
                  ctx.strokeStyle = iconBorderColor;
                  ctx.stroke();
                }
                ctx.closePath();

                let imgWidth = customImage.width;
                let imgHeight = customImage.height;
                let scaleFactor = ((Math.max(canvas.width, canvas.height) / 2) - 1) * 2 / Math.max(imgWidth, imgHeight) / 1.5;
                let newWidth = imgWidth * scaleFactor;
                let newHeight = imgHeight * scaleFactor;
                let xPos = (canvas.width / 2) - newWidth / 2;
                let yPos = (canvas.height / 2) - newHeight / 2;

                ctx.save();
                ctx.beginPath();
                ctx.arc((canvas.width / 2), (canvas.height / 2), ((canvas.width / 2) - 1), 0, 2 * Math.PI, false);
                ctx.clip();
                ctx.drawImage(customImage, xPos, yPos, newWidth, newHeight);
                ctx.closePath();
                ctx.restore();

                let imageUrl = canvas.toDataURL();
                textGeoObject.options.set('iconImageSize', [size, size]);
                textGeoObject.options.set('iconImageHref', imageUrl);
                textGeoObject.properties.set('iconImage', imageUrl)
                textGeoObject.options.set(
                  'iconLayout',
                  ymaps.templateLayoutFactory.createClass(
                    `<div class="polygon-content${nameDirection === 'vertical' ? ' polygon-content--vertical' : ''}">
                  <div class="polygon-content__icon" style="${iconExistence === 'none' ? 'display: none' : ''}"><img src="$[properties.iconImage]" width="${size}" height="${size}"></div>
                  <div class="polygon-content__text" style="${!nameShow ? 'display: none' : ''}; font-size: ${nameFontSize}px; color: ${nameColor}; ${iconExistence !== 'none' && nameDirection === 'vertical' ? 'transform: rotate(-90deg) translateX(95px)' : ''};">
                    $[properties.label]
                  </div>
                </div>`
                  )
                );
              };

              customImage.src = imgUrl;
            } else {
              textGeoObject.options.set(
                'iconLayout',
                ymaps.templateLayoutFactory.createClass(
                  `<div class="polygon-content${nameDirection === 'vertical' ? ' polygon-content--vertical' : ''}">
                  <div class="polygon-content__icon" style="${iconExistence === 'none' ? 'display: none' : ''}"><img src="${require('@/assets/svg/location.svg')}" width="${iconSize}" height="${iconSize}"></div>
                  <div class="polygon-content__text" style="${!nameShow ? 'display: none' : ''}; font-size: ${nameFontSize}px; color: ${nameColor}; ${iconExistence !== 'none' && nameDirection === 'vertical' ? 'transform: rotate(-90deg) translateX(95px)' : ''};">
                    $[properties.label]
                  </div>
                </div>`
                )
              );
            }
          }

          if (bgCollectionID || bgID) {
            const opacity = this.styling ? (bgCollectionID || bgID) && (this.pictoByObj[bgCollectionID] || this.pictoByObj[bgID]) && bgOpacity / 100 : 0.2
            let canvas = document.createElement('canvas');
            let ctx = canvas.getContext('2d');
            let imageObj = new Image();
            imageObj.crossOrigin = 'Anonymous';

            imageObj.onload = function() {
              if (imageObj?.src?.length) {
                canvas.width = imageObj.width >= imageObj.height ? imageObj.width : imageObj.height;
                canvas.height = imageObj.width >= imageObj.height ? imageObj.width : imageObj.height;
                let scaleFactor = Math.max(canvas.width / imageObj.width, canvas.height / imageObj.height);
                let newWidth = imageObj.width * scaleFactor;
                let newHeight = imageObj.height * scaleFactor;
                let xPos = (canvas.width - newWidth) / 2;
                let yPos = (canvas.height - newHeight) / 2;

                ctx.drawImage(imageObj, xPos, yPos, newWidth, newHeight);

                let imageUrl = canvas.toDataURL();
                geoObject.options.set('fillImageHref', imageUrl);
                geoObject.options.set('fillOpacity', opacity);
              }
            };

            if (this.pictoByObj[bgCollectionID]) {
              imageObj.src = this.pictoByObj[bgCollectionID] ? this.$addServerPath(`${this.STORAGE_BACKGROUND_PATH}${this.pictoByObj[bgCollectionID]}`) : '';
            }

            if (this.pictoByObj[bgID]) {
              imageObj.src = this.pictoByObj[bgID];
            }
          }
          break;
        case "polyline":
          geoObject = new ymaps.Polyline(coordinates, {
            uuid: geoObjectUuid,
          }, polylineOptions);
          geoObject.properties.set('center-of-mass', centerOfMass(lineString(coordinates)))

          if (borderShadow) {
            shadowGeoObject = new ymaps.Polyline(coordinates, {
              shadowUuid: geoObjectUuid,
            }, polylineShadowOptions);
          }
          if (object?.label) {
            textGeoObject = new ymaps.Placemark(centerOfMass(lineString(coordinates)).geometry.coordinates, { label: object?.label }, {
              iconLayout: 'default#image',
              iconImageHref: '',
              iconImageSize: [0, 0],
              zIndex: 500 + (object?.conditionIndex || 0)
            })
            let imgUrl = '';

            if (object?.type?.label?.toLowerCase() === 'событие') {
              switch (iconExistence) {
              case 'own':
                imgUrl = iconCollectionID ? addServerPath(`/admin/icon_files/${iconCollectionID}/picto_black/`) : ''
                break;
              case 'type':
                imgUrl = object?.type?.icon ? addServerPath(`/admin/icon_files/${object?.type?.icon}/picto_black/`) : '';
                break;
              case 'photo':
                imgUrl = object?.event_icon ? addServerPath(`/admin/icon_files/${object?.event_icon}/picto_black/`) : '';
                break;
              }
            } else {
              switch (iconExistence) {
              case 'own':
                imgUrl = iconCollectionID ? addServerPath(`/admin/icon_files/${iconCollectionID}/picto_black/`) : ''
                break;
              case 'type':
                imgUrl = object?.type?.icon ? addServerPath(`/admin/icon_files/${object?.type?.icon}/picto_black/`) : iconCollectionID ? addServerPath(`/admin/icon_files/${iconCollectionID}/picto_black/`) : '';
                break;
              case 'photo':
                if (object?.icon) {
                  imgUrl = addServerPath(`/admin/icon_files/${object?.icon}/picto_black/`)
                } else if (object?.has_image) {
                  imgUrl = addServerPath(`/public/object_images_files/first_image/${object?.id}/?preview=1`)
                } else if (object?.type?.icon) {
                  imgUrl = addServerPath(`/admin/icon_files/${object?.type?.icon}/picto_black/`)
                } else if (iconCollectionID) {
                  imgUrl = addServerPath(`/admin/icon_files/${iconCollectionID}/picto_black/`)
                }
                break;
              }
            }

            if(imgUrl.length) {
              let customImage = new Image();
              customImage.crossOrigin = "Anonymous";
              customImage.onload = function () {

                let canvas = document.createElement('canvas');
                let size = iconSize;
                canvas.width = size;
                canvas.height = size;
                let ctx = canvas.getContext('2d');

                ctx.beginPath();
                ctx.arc(canvas.width / 2, canvas.height / 2, (canvas.width / 2) - 1, 0, 2 * Math.PI, false);
                if (iconBackgroundColor) {
                  ctx.fillStyle = iconBackgroundColor;
                  ctx.fill();
                }
                if (iconBorderColor) {
                  ctx.lineWidth = 2;
                  ctx.strokeStyle = iconBorderColor;
                  ctx.stroke();
                }
                ctx.closePath();

                let imgWidth = customImage.width;
                let imgHeight = customImage.height;
                let scaleFactor = ((Math.max(canvas.width, canvas.height) / 2) - 1) * 2 / Math.max(imgWidth, imgHeight) / 1.5;
                let newWidth = imgWidth * scaleFactor;
                let newHeight = imgHeight * scaleFactor;
                let xPos = (canvas.width / 2) - newWidth / 2;
                let yPos = (canvas.height / 2) - newHeight / 2;

                ctx.save();
                ctx.beginPath();
                ctx.arc((canvas.width / 2), (canvas.height / 2), ((canvas.width / 2) - 1), 0, 2 * Math.PI, false);
                ctx.clip();
                ctx.drawImage(customImage, xPos, yPos, newWidth, newHeight);
                ctx.closePath();
                ctx.restore();

                let imageUrl = canvas.toDataURL();
                textGeoObject.options.set('iconImageSize', [size, size]);
                textGeoObject.options.set('iconImageHref', imageUrl);
                textGeoObject.properties.set('iconImage', imageUrl)
                textGeoObject.options.set(
                  'iconLayout',
                  ymaps.templateLayoutFactory.createClass(
                    `<div class="polyline-content${nameDirection === 'vertical' ? ' polyline-content--vertical' : ''}">
                  <div class="polyline-content__icon" style="${iconExistence === 'none' ? 'display: none' : ''}"><img src="$[properties.iconImage]" width="${size}" height="${size}"></div>
                  <div class="polyline-content__text" style="${!nameShow ? 'display: none' : ''}; font-size: ${nameFontSize}px; color: ${nameColor}; ${iconExistence !== 'none' && nameDirection === 'vertical' ? 'transform: rotate(-90deg) translateX(95px)' : ''};">
                    $[properties.label]
                  </div>
                </div>`
                  )
                );
              };

              customImage.src = imgUrl;
            } else {
              textGeoObject.options.set(
                'iconLayout',
                ymaps.templateLayoutFactory.createClass(
                  `<div class="polyline-content${nameDirection === 'vertical' ? ' polyline-content--vertical' : ''}">
                  <div class="polyline-content__icon" style="${iconExistence === 'none' ? 'display: none' : ''}"><img src="${require('@/assets/svg/location.svg')}" width="${iconSize}" height="${iconSize}"></div>
                  <div class="polyline-content__text" style="${!nameShow ? 'display: none' : ''}; font-size: ${nameFontSize}px; color: ${nameColor}; ${iconExistence !== 'none' && nameDirection === 'vertical' ? 'transform: rotate(-90deg) translateX(95px)' : ''};">
                    $[properties.label]
                  </div>
                </div>`
                )
              );
            }
          }
          break;
        case "circle":
          geoObject = new ymaps.Placemark(coordinates, {
            uuid: geoObjectUuid,
            iconContent: object?.label,
          }, circleOptions);

          if(object?.typeViewPlacemark ?? this.typeViewPlacemark != 'circle'){
            let imgUrl = '';

            if (object?.type?.label?.toLowerCase() === 'событие') {
              switch (object?.typeViewPlacemark ?? this.typeViewPlacemark) {
              case 'own':
              case 'all':
                imgUrl = iconCollectionID ? addServerPath(`/admin/icon_files/${iconCollectionID}/picto_black/`) : ''
                break;
              case 'type':
                imgUrl = object?.type?.icon ? addServerPath(`/admin/icon_files/${object?.type?.icon}/picto_black/`) : '';
                break;
              case 'photo':
                imgUrl = object?.event_icon ? addServerPath(`/admin/icon_files/${object?.event_icon}/picto_black/`) : '';
                break;
              }
            } else {
              switch (object?.typeViewPlacemark ?? this.typeViewPlacemark) {
              case 'own':
              case 'all':
                imgUrl = iconCollectionID ? addServerPath(`/admin/icon_files/${iconCollectionID}/picto_black/`) : ''
                break;
              case 'type':
                imgUrl = object?.type?.icon ? addServerPath(`/admin/icon_files/${object?.type?.icon}/picto_black/`) : iconCollectionID ? addServerPath(`/admin/icon_files/${iconCollectionID}/picto_black/`) : '';
                break;
              case 'photo':
                if (object?.icon) {
                  imgUrl = addServerPath(`/admin/icon_files/${object?.icon}/picto_black/`)
                } else if (object?.has_image) {
                  imgUrl = addServerPath(`/public/object_images_files/first_image/${object?.id}/?preview=1`)
                } else if (object?.type?.icon) {
                  imgUrl = addServerPath(`/admin/icon_files/${object?.type?.icon}/picto_black/`)
                } else if (iconCollectionID) {
                  imgUrl = addServerPath(`/admin/icon_files/${iconCollectionID}/picto_black/`)
                }
                break;
              }
            }

            if(imgUrl.length) {
              let customImage = new Image();
              customImage.crossOrigin = "Anonymous";
              customImage.onload = function () {

                let canvas = document.createElement('canvas');
                let size = iconSize;
                canvas.width = size;
                canvas.height = size;
                let ctx = canvas.getContext('2d');

                ctx.beginPath();
                ctx.arc(canvas.width / 2, canvas.height / 2, (canvas.width / 2) - 1, 0, 2 * Math.PI, false);
                if (iconBackgroundColor) {
                  ctx.fillStyle = iconBackgroundColor;
                  ctx.fill();
                }
                if (iconBorderColor) {
                  ctx.lineWidth = 2;
                  ctx.strokeStyle = iconBorderColor;
                  ctx.stroke();
                }
                ctx.closePath();

                let imgWidth = customImage.width;
                let imgHeight = customImage.height;
                let scaleFactor = ((Math.max(canvas.width, canvas.height) / 2) - 1) * 2 / Math.max(imgWidth, imgHeight) / 1.5;
                let newWidth = imgWidth * scaleFactor;
                let newHeight = imgHeight * scaleFactor;
                let xPos = (canvas.width / 2) - newWidth / 2;
                let yPos = (canvas.height / 2) - newHeight / 2;

                ctx.save();
                ctx.beginPath();
                ctx.arc((canvas.width / 2), (canvas.height / 2), ((canvas.width / 2) - 1), 0, 2 * Math.PI, false);
                ctx.clip();
                ctx.drawImage(customImage, xPos, yPos, newWidth, newHeight);
                ctx.closePath();
                ctx.restore();

                let imageUrl = canvas.toDataURL();
                geoObject.options.set('iconImageSize', [size, size]);
                geoObject.options.set('iconImageHref', imageUrl);
                geoObject.properties.set('iconImage', imageUrl)

                if (object) {
                  geoObject.options.set(
                    'iconLayout',
                    ymaps.templateLayoutFactory.createClass(
                      `<div class="map-object${nameDirection === 'vertical' ? ' map-object--vertical' : ''}">
                      <div class="map-object__img">
                        <img src="$[properties.iconImage]" width="${size}" height="${size}" alt="">
                      </div>
                      <div class="map-object__caption" style="${!nameShow ? 'display: none' : ''}; font-size: ${nameFontSize}px; color: ${nameColor}; ${nameDirection === 'vertical' ? `transform: rotate(-90deg) translateX(${(iconSize / 2) + 44}px)` : ''}">$[properties.iconContent]</div>
                    </div>`
                    )
                  );
                }

                if (iconBackgroundColor && iconShadow) {
                  ctx.fillStyle = 'rgba(0, 0, 0, 0.4';
                  ctx.fill();
                  let shadowImageUrl = canvas.toDataURL();
                  geoObject.options.set('iconShadowImageHref', shadowImageUrl);
                } else if ((!iconBackgroundColor && !iconBorderColor) && iconShadow) {
                  geoObject.options.set('iconShadowImageHref', imageUrl);
                  geoObject.options.set('iconShadowOpacity', 0.2);
                }
              };

              customImage.src = imgUrl;
            } else {
              if (object) {
                geoObject.options.set(
                  'iconLayout',
                  ymaps.templateLayoutFactory.createClass(
                    `<div class="map-object${nameDirection === 'vertical' ? ' map-object--vertical' : ''}">
                    <div class="map-object__img">
                      <img src="${require('@/assets/svg/location.svg')}" width="${iconSize}" height="${iconSize}" alt="">
                    </div>
                    <div class="map-object__caption" style="${!nameShow ? 'display: none' : ''}; font-size: ${nameFontSize}px; color: ${nameColor}; ${nameDirection === 'vertical' ? `transform: rotate(-90deg) translateX(${(iconSize / 2) + 44}px)` : ''}">$[properties.iconContent]</div>
                  </div>`
                  )
                );
              }
            }
          }

          break;
        }

        if (geoObject) {
          if (!styles?.hasNotSnoska && object) {
            const imgPath = object?.has_image
              ? addServerPath(`/public/object_images_files/first_image/${object?.id}/?preview=1`)
              : object?.event_icon ? addServerPath(`/admin/icon_files/${object?.event_icon}/picto_black/`)
                : object?.icon ? addServerPath(`/admin/icon_files/${object?.icon}/picto_black/`) : ''

            geoObject.properties.set('balloonContent', `<a class="map-view__baloon-link" href="/objects/${object?.slug}?from=objScheme">
                ${imgPath && !object?.relatedAttrId ?
    `<div class="map-view__baloon-img-wrapper"><img class="map-view__baloon-img${!object?.has_image && object?.event_icon || !object?.has_image && object?.icon ? ' map-view__baloon-img--icon' : ''}" src="${imgPath}" width="140" height="105" alt="${object?.label}"></div>` : ''}
              <span class="map-view__baloon-label">${object?.label}</span>
              <div class="map-view__baloon-text">${object?.text}</div>
              </a>`);
            geoObject.options.set('iconShape', { type: 'Circle', coordinates: [0, 0], radius: iconSize / 2 })
          }

          if (shadowGeoObject) {
            this.geoCollection.add(shadowGeoObject)
          }
          if (textGeoObject) {
            this.geoCollection.add(textGeoObject)
          }
          this.instance.geoObjects.add(this.geoCollection)

          resolve(geoObject)
        }
      })
    },
    getArea(geometry) {
      return area(bboxPolygon([...geometry[0], ...geometry[1]]))
    },
    async checkZoom() {
      // const mapBounds = this.instance.getBounds()
      // const mapViewArea = this.getArea(mapBounds)

      this.instance.geoObjects.each(async (geoObject) => {
        const rawData = this.globalGeometry.find(x => {
          return x.uuid === geoObject.properties.get('uuid')
        })

        if (rawData) {
          if (['polygon', 'polyline'].includes(rawData.type)) {
            // const rawBounds = rawData.bounds

            // const needUseSmall = mapViewArea / this.getArea(rawBounds) > 150
            // if (needUseSmall && geoObject.geometry.getType() === 'Point') return

            this.instance.geoObjects.remove(geoObject)
            // if (needUseSmall) {
            //   this.geoCollection.removeAll()
            //   this.instance.geoObjects.remove(this.geoCollection)
            // }

            // if (needUseSmall) {
            //   this.buildGeoObject('circle', rawData['center-of-mass'], rawData.uuid, rawData.object, rawData.styles).then((newGeoObject) => {
            //     this.instance.geoObjects.add(newGeoObject)
            //   })
            // } else {
            if (!rawData.styles?.unShow) {
              this.buildGeoObject(rawData.type, rawData.geometry, rawData.uuid, rawData.object, rawData.styles).then((newGeoObject) => {
                this.instance.geoObjects.add(newGeoObject)
              })
            }
            // }

          }
        }
      })
    },
    async init() {
      this.instance = new ymaps.Map(this.id, {
        center: [55.76, 37.64],
        zoom: 7,
        controls: ['zoomControl']
      }, {
        suppressMapOpenBlock: true
      });

      if ('ontouchstart' in window) {
        this.instance.behaviors.disable('drag');
      }
      if (!this.zoom) {
        this.instance.behaviors.disable('scrollZoom');
      }

      this.geoCollection = new ymaps.GeoObjectCollection();

      this.canvas = document.createElement('canvas');
      let ctx = this.canvas.getContext('2d');
      const mapContainer = this.$refs["map-frame"];
      let canvasSize
      const offset = 16
      const containerWidth = mapContainer.clientWidth - offset


      if (this.schemeStyles?.cover?.collectionID) {
        await this.getPictoByObj(this.schemeStyles.cover.collectionID, 'collection');
      }
      if (this.schemeStyles?.cover?.bgID) {
        await this.getPictoByObj(this.schemeStyles.cover.bgID, 'upload');
      }

      const geoObjectsList = []
      for (const item of this.globalGeometry) {
        const geo = item.type === 'circle' && Array.isArray(item.geometry[0])
          ? item.geometry[0][0]
          : item.geometry

        if (item.styles?.background?.collectionID) {
          await this.getPictoByObj(item.styles.background.collectionID, 'collection');
        }
        if (item.styles?.background?.bgID) {
          await this.getPictoByObj(item.styles.background.bgID, 'upload');
        }
        if (!item.styles?.unShow) {
          await this.buildGeoObject(item.type, geo, item.uuid, item.object, item.styles).then((geoObject) => {
            this.instance.geoObjects.add(geoObject)
            geoObjectsList.push(geoObject)
          })
        }
      }

      await this.setBounds()

      let isTimeoutComplete = false
      const timeout = setTimeout(async () => {
        const mapSizeCoords = this.instance.geoObjects.getBounds()
        if (mapSizeCoords) {
          const firstPoint = [mapSizeCoords[0][0], mapSizeCoords[0][1]]
          const lastPoint = [mapSizeCoords[1][0], mapSizeCoords[1][1]]

          const width = lastPoint[1] - firstPoint[1]
          const height = Math.abs(((lastPoint[0] - firstPoint[0]) / Math.cos((lastPoint[0] - firstPoint[0]))) * 2)
          const k = height / width
          const containerHeight = Math.round(containerWidth * k)

          if (containerHeight > containerWidth * 1.2) {
            this.canvas.width = containerWidth + offset
            this.canvas.height = Math.round(containerWidth * 1.2) + offset
            canvasSize = { width: containerWidth + offset, height: Math.round(containerWidth * 1.2) + offset }
          } else {
            this.canvas.width = containerWidth + offset
            this.canvas.height = containerHeight / 1.2 + offset
            canvasSize = { width: containerWidth + offset, height: containerHeight / 1.2 + offset }
          }

          this.schemeData.polygons.coordinates = {
            width: canvasSize.width,
            height: canvasSize.height,
            left: 0,
            top: 0,
          }
          this.schemeData.polygons.aspectRatio = canvasSize.width / canvasSize.height
          this.schemeData.polygons.polygons = []

          // Подложка схемы
          await (async () => {
            return new Promise((resolve) => {
              const bgCollectionID = this.schemeStyles?.cover?.collectionID;
              const bgID = this.schemeStyles?.cover?.bgID;
              if (bgCollectionID || bgID) {
                try {
                  const tempCanvas = document.createElement('canvas');
                  const tempCtx = tempCanvas.getContext('2d');

                  const img = new Image()
                  img.crossOrigin = 'Anonymous';
                  img.onload = () => {
                    if (img?.src?.length) {
                      tempCanvas.width = canvasSize.width;
                      tempCanvas.height = canvasSize.height;
                      tempCtx.rect(0, 0, tempCanvas.width, tempCanvas.height)
                      let scaleFactor = Math.max(tempCanvas.width / img.width, tempCanvas.height / img.height);
                      let newWidth = img.width * scaleFactor;
                      let newHeight = img.height * scaleFactor;
                      let xPos = (tempCanvas.width - newWidth) / 2;
                      let yPos = (tempCanvas.height - newHeight) / 2;

                      tempCtx.clip()
                      tempCtx.drawImage(img, xPos, yPos, newWidth, newHeight);
                      ctx.save()
                      ctx.rect(0, 0, tempCanvas.width, tempCanvas.height)
                      ctx.clip()
                      ctx.fillStyle = '#ffffff'
                      ctx.fill()
                      ctx.filter = `opacity(${this.schemeStyles.cover.opacity / 100})`
                      ctx.drawImage(tempCanvas, 0, 0, tempCanvas.width, tempCanvas.height);
                      ctx.restore()
                      resolve()
                    }
                  }

                  if (this.pictoByObj[bgCollectionID]) {
                    img.src = this.pictoByObj[bgCollectionID] ? this.$addServerPath(`${this.STORAGE_BACKGROUND_PATH}${this.pictoByObj[bgCollectionID]}`) : ''
                  }

                  if (this.pictoByObj[bgID]) {
                    img.src = this.pictoByObj[bgID]
                  }
                } catch (err) {
                  ctx.rect(0, 0, canvasSize.width, canvasSize.height)
                  ctx.fillStyle = `rgba(${this.hexToRgb(this.schemeStyles?.cover?.color ?? '#ffffff')}, ${this.schemeStyles?.cover?.opacity / 100})`
                  ctx.fill()
                  resolve()
                }
              } else {
                ctx.rect(0, 0, canvasSize.width, canvasSize.height)
                ctx.fillStyle = `rgba(${this.hexToRgb(this.schemeStyles?.cover?.color ?? '#ffffff')}, ${this.schemeStyles?.cover?.opacity / 100})`
                ctx.fill()
                resolve()
              }
            })
          })()

          for (const item of this.globalGeometry) {
            for (const geoObject of geoObjectsList) {
              const rawData = item.uuid === geoObject.properties.get('uuid') ? geoObject : null
              if (rawData && this.showScheme) {
                if (!item.styles?.unShow) {
                  const snoska = item.styles?.hasNotSnoska === false ? true : false

                  switch (item.type) {
                  case 'polygon':
                    if (item.object && item.object?.typeViewPlacemark !== 'all') {
                      await this.drawCircle(rawData, canvasSize, ctx, item, snoska);
                    } else {
                      await this.drawPolygon(rawData, canvasSize, ctx, item, snoska);
                    }
                    break;
                  case 'polyline':
                    if (item.object && item.object?.typeViewPlacemark !== 'all') {
                      await this.drawCircle(rawData, canvasSize, ctx, item, snoska);
                    } else {
                      await this.drawPolyline(rawData, canvasSize, ctx, item, snoska);
                    }
                    break;
                  case 'circle':
                    await this.drawCircle(rawData, canvasSize, ctx, item, snoska);
                    break;
                  }
                }
              }
            }
          }

          await this.checkZoom()

          this.instance.events.add('boundschange', async (event) => {
            if (event.get('newZoom') !== event.get('oldZoom')) {
              await this.checkZoom()
            }
          })
        }

        if (this.showScheme) {
          this.$emit('save-canvas', { canvas: this.canvas, data: this.schemeData })
        }

        isTimeoutComplete = true
      }, 300)
      if (isTimeoutComplete) {
        clearTimeout(timeout)
      }
    },
    mapToCanvas(point, canvasSize, item = false, isCircle = false) {
      const mapSizeCoords = this.instance.geoObjects.getBounds()
      const firstPoint = [mapSizeCoords[0][0], mapSizeCoords[0][1]]
      const lastPoint = [mapSizeCoords[1][0], mapSizeCoords[1][1]]
      const offset = 16
      point = [point[0], point[1]]

      const width = lastPoint[1] - firstPoint[1]
      const height = lastPoint[0] - firstPoint[0]

      let percentX = (point[1] - firstPoint[1]) / width
      let percentY = (lastPoint[0] - point[0]) / height

      if (isCircle) {
        const iconSize = item && item.styles && item.styles.icon && item.styles.icon.size ? item.styles.icon.size : 36
        const x = Math.trunc((canvasSize.width - offset) * (isNaN(percentX) ? 0 : percentX)) - iconSize / 2
        const y = Math.trunc((canvasSize.height - offset) * (isNaN(percentY) ? 0 : percentY)) - iconSize / 2
        return { x: x + (offset / 2), y: y + (offset / 2) }

      } else {
        const x = Math.trunc((canvasSize.width - offset) * percentX)
        const y = Math.trunc((canvasSize.height - offset) * percentY)

        return { x: x + (offset / 2), y: y + (offset / 2) }
      }
    },
    async drawPolygon(polygon, canvasSize, ctx, item, withSnoska = true) {
      return new Promise(async resolve => {
        if (!item.styles?.unShow) {
          if (polygon.geometry) {
            const bgType = item.styles.background.type
            const bgCollectionID = item.styles.background.collectionID
            const bgID = item.styles.background.bgID
            const points = polygon.geometry.getCoordinates()[0]
            ctx.beginPath()
            let convertedPolygon
            if (withSnoska) {
              convertedPolygon = {
                id: item.uuid,
                type: 'polygon',
                anchors: [],
                description: {
                  text: '',
                  object: item?.object,
                  object_id: item?.object?.id
                }
              }
            }
            let firstPoint = true
            points.forEach(point => {
              const canvasPoint = this.mapToCanvas(point, canvasSize, false)
              if (firstPoint) {
                ctx.moveTo(canvasPoint.x, canvasPoint.y)
                firstPoint = false
              } else {
                ctx.lineTo(canvasPoint.x, canvasPoint.y)
              }
              if (withSnoska) {
                convertedPolygon.anchors.push({
                  attrs: {
                    x: canvasPoint.x,
                    y: canvasPoint.y,
                    radius: 4,
                    fill: '#328FE6',
                    draggable: true,
                    polygon_id: convertedPolygon.id,
                    start: true,
                    // uuid: uuidv4(),
                    type: 'anchor',
                    hitStrokeWidth: 10,
                    scaleX: 0.5350454788657036,
                    scaleY: 0.5350454788657036
                  },
                  className: 'Circle'
                })
              }
            })
            if (withSnoska) {
              this.schemeData.polygons.polygons.push(convertedPolygon)
            }

            if (bgType !== 'image') {
              ctx.fillStyle = `rgba(${this.hexToRgb(item.styles.background.color ?? '#328fe5')}, ${item.styles.background.opacity / 100})`
            }
            ctx.strokeStyle = item.styles.border.color ?? '#328FE6'
            ctx.lineWidth = item.styles.border.line !== 'none' && item.styles.border.lineWidth ? item.styles.border.lineWidth : null
            ctx.lineJoin = 'round'
            ctx.lineCap = 'round'
            this.applyCanvasLineDash(ctx, item.styles.border.line)
            ctx.stroke()
            ctx.fill()
            ctx.closePath()

            if (bgType === 'color') {
              if (item.styles.icon.existence !== 'none') {
                await this.drawCircle(polygon, canvasSize, ctx, item, false)
              }

              let canvasPoints = []
              let minX = Infinity
              let minY = Infinity
              let maxX = 0
              let maxY = 0

              points.forEach(point => {
                const canvasPoint = this.mapToCanvas(point, canvasSize, false)
                canvasPoints.push(canvasPoint)
                minX = canvasPoint.x < minX ? canvasPoint.x : minX
                minY = canvasPoint.y < minY ? canvasPoint.y : minY
                maxX = canvasPoint.x > maxX ? canvasPoint.x : maxX
                maxY = canvasPoint.y > maxY ? canvasPoint.y : maxY
              })
              const tempCanvas = document.createElement('canvas');
              const tempCtx = tempCanvas.getContext('2d');

              const img = new Image()
              img.crossOrigin = 'Anonymous';
              img.onload = () => {
                if (img?.src?.length) {
                  tempCanvas.width = maxX - minX;
                  tempCanvas.height = maxY - minY;

                  tempCtx.beginPath()
                  let tempFirstPoint = true
                  canvasPoints.forEach(canvasPoint => {
                    if (tempFirstPoint) {
                      tempCtx.moveTo(canvasPoint.x - minX, canvasPoint.y - minY)
                      tempFirstPoint = false
                    } else {
                      tempCtx.lineTo(canvasPoint.x - minX, canvasPoint.y - minY)
                    }
                  })

                  let scaleFactor = Math.max(tempCanvas.width / img.width, tempCanvas.height / img.height);
                  let newWidth = img.width * scaleFactor;
                  let newHeight = img.height * scaleFactor;
                  let xPos = (tempCanvas.width - newWidth) / 2;
                  let yPos = (tempCanvas.height - newHeight) / 2;

                  tempCtx.clip()
                  tempCtx.fillStyle = `rgba(255, 255, 255, 1)`
                  tempCtx.fill()
                  tempCtx.drawImage(img, xPos, yPos, newWidth, newHeight);
                  tempCtx.closePath()
                  ctx.save()
                  ctx.beginPath()
                  let firstPoint = true
                  canvasPoints.forEach(canvasPoint => {
                    if (firstPoint) {
                      ctx.moveTo(canvasPoint.x, canvasPoint.y)
                      firstPoint = false
                    } else {
                      ctx.lineTo(canvasPoint.x, canvasPoint.y)
                    }
                  })
                  ctx.clip()
                  ctx.fillStyle = `rgba(255, 255, 255, 1)`
                  ctx.fill()
                  ctx.filter = `opacity(${item.styles.background.opacity / 100})`
                  ctx.drawImage(tempCanvas, minX, minY, maxX - minX, maxY - minY);
                  ctx.closePath()
                  ctx.restore()

                  resolve()
                }
              }

              if (this.pictoByObj[bgCollectionID]) {
                img.src = this.pictoByObj[bgCollectionID] ? this.$addServerPath(`${this.STORAGE_BACKGROUND_PATH}${this.pictoByObj[bgCollectionID]}`) : ''
              }

              if (this.pictoByObj[bgID]) {
                img.src = this.pictoByObj[bgID]
              }
            }

            if (item.styles.border.shadow === 'has') {
              ctx.save()
              ctx.beginPath()
              let firstPoint = true
              points.forEach(point => {
                const canvasPoint = this.mapToCanvas(point, canvasSize, false)

                if (firstPoint) {
                  ctx.moveTo(canvasPoint.x, canvasPoint.y)
                  firstPoint = false
                } else {
                  ctx.lineTo(canvasPoint.x, canvasPoint.y)
                }
              })
              ctx.strokeStyle = item.styles.border.color ? `rgba(${this.hexToRgb(item.styles.border.color)}, 0.2)` : '#328FE620'
              ctx.lineWidth = item.styles.border.line !== 'none' && item.styles.border.lineWidth ? item.styles.border.lineWidth + 10 : null
              this.applyCanvasLineDash(ctx, item.styles.border.line)
              ctx.stroke()
              ctx.closePath()
              ctx.restore()
            }

            if (!bgCollectionID && !bgID) {
              if (item.styles.icon.existence !== 'none') {
                await this.drawCircle(polygon, canvasSize, ctx, item, false)
              }

              resolve()
            }
          }
        } else {
          resolve()
        }
      })
    },
    async drawPolyline(polyline, canvasSize, ctx, item, withSnoska = true) {
      return new Promise(async resolve => {
        if (!item.styles?.unShow) {
          if (polyline.geometry) {
            const points = polyline.geometry.getCoordinates()
            ctx.beginPath()
            let convertedPolyline
            if (withSnoska) {
              convertedPolyline = {
                id: item.uuid,
                type: 'line',
                anchors: [],
                description: {
                  text: '',
                  object: item?.object,
                  object_id: item?.object?.id
                }
              }
            }
            let firstPoint = true
            points.forEach(point => {
              const canvasPoint = this.mapToCanvas(point, canvasSize, false)
              if (firstPoint) {
                ctx.moveTo(canvasPoint.x, canvasPoint.y)
                firstPoint = false
              } else {
                ctx.lineTo(canvasPoint.x, canvasPoint.y)
              }
              if (withSnoska) {
                convertedPolyline.anchors.push({
                  attrs: {
                    x: canvasPoint.x,
                    y: canvasPoint.y,
                    radius: 4,
                    fill: '#328FE6',
                    draggable: true,
                    polygon_id: convertedPolyline.id,
                    start: true,
                    // uuid: uuidv4(),
                    type: 'anchor',
                    hitStrokeWidth: 10,
                    scaleX: 1.050420168067227,
                    scaleY: 1.050420168067227
                  },
                  className: 'Circle'
                })
              }
            })
            if (withSnoska) {
              this.schemeData.polygons.polygons.push(convertedPolyline)
            }

            ctx.strokeStyle = item.styles.border.color ?? '#328FE6'
            ctx.lineWidth = item.styles.border.line !== 'none' && item.styles.border.lineWidth ? item.styles.border.lineWidth : 2
            ctx.lineJoin = 'round'
            ctx.lineCap = 'round'
            this.applyCanvasLineDash(ctx, item.styles.border.line)
            ctx.stroke()
            ctx.closePath()

            if (item.styles.border.shadow === 'has') {
              ctx.beginPath()
              firstPoint = true
              points.forEach(point => {
                const canvasPoint = this.mapToCanvas(point, canvasSize, false)
                if (firstPoint) {
                  ctx.moveTo(canvasPoint.x, canvasPoint.y)
                  firstPoint = false
                } else {
                  ctx.lineTo(canvasPoint.x, canvasPoint.y)
                }
              })
              ctx.strokeStyle = item.styles.border.color ? `rgba(${this.hexToRgb(item.styles.border.color)}, 0.2)` : '#328FE620'
              ctx.lineWidth = item.styles.border.line !== 'none' && item.styles.border.lineWidth ? item.styles.border.lineWidth + 10 : null
              this.applyCanvasLineDash(ctx, item.styles.border.line)
              ctx.stroke()
            }

            if (item.styles.icon.existence !== 'none') {
              await this.drawCircle(polyline, canvasSize, ctx, item, false)
            }

            resolve()
          }
        } else {
          resolve()
        }
      })
    },
    async drawCircle(circle, canvasSize, ctx, item, withSnoska = true) {
      return new Promise(resolve => {
        if (!item.styles?.unShow) {
          if (circle.geometry) {
            const point = item.type !== 'circle' ? circle.properties.get('center-of-mass').geometry.coordinates : circle.geometry.getCoordinates();
            const canvasPoint = this.mapToCanvas(point, canvasSize, item, true);
            const iconSize = item && item.styles && item.styles.icon && item.styles.icon.size ? item.styles.icon.size : 36

            if (withSnoska) {
              const convertedPoint = {
                id: item.uuid,
                type: 'point',
                anchors: [],
                description: {
                  text: '',
                  object: item?.object,
                  object_id: item?.object?.id
                }
              }
              convertedPoint.anchors.push({
                attrs: {
                  x: canvasPoint.x + iconSize / 2,
                  y: canvasPoint.y + iconSize / 2,
                  radius: 10,
                  fill: '#328FE6',
                  draggable: true,
                  polygon_id: convertedPoint.id,
                  start: true,
                  // uuid: uuidv4(),
                  type: 'anchor',
                  hitStrokeWidth: 10,
                  scaleX: 1.050420168067227,
                  scaleY: 1.050420168067227
                },
                className: 'Circle'
              })
              this.schemeData.polygons.polygons.push(convertedPoint)
            }

            const iconCollectionID = item.styles.icon.collectionID

            if (item?.object?.typeViewPlacemark ?? this.typeViewPlacemark != 'circle') {
              let imgUrl = '';

              try {
                if (item?.object?.type?.label?.toLowerCase() === 'событие') {
                  switch (item?.object?.typeViewPlacemark ?? this.typeViewPlacemark) {
                  case 'own':
                  case 'all':
                    imgUrl = iconCollectionID ? addServerPath(`/admin/icon_files/${iconCollectionID}/picto_black/`) : ''
                    break;
                  case 'type':
                    imgUrl = item?.object?.type?.icon ? addServerPath(`/admin/icon_files/${item?.object?.type?.icon}/picto_black/`) : '';
                    break;
                  case 'photo':
                    imgUrl = item?.object?.event_icon ? addServerPath(`/admin/icon_files/${item?.object?.event_icon}/picto_black/`) : '';
                    break;
                  }
                } else {
                  switch (item?.object?.typeViewPlacemark ?? this.typeViewPlacemark) {
                  case 'own':
                  case 'all':
                    imgUrl = iconCollectionID ? addServerPath(`/admin/icon_files/${iconCollectionID}/picto_black/`) : ''
                    break;
                  case 'type':
                    imgUrl = item?.object?.type?.icon ? addServerPath(`/admin/icon_files/${item?.object?.type?.icon}/picto_black/`) : iconCollectionID ? addServerPath(`/admin/icon_files/${iconCollectionID}/picto_black/`) : '';
                    break;
                  case 'photo':
                    if (item?.object?.icon) {
                      imgUrl = addServerPath(`/admin/icon_files/${item?.object?.icon}/picto_black/`)
                    } else if (item?.object?.has_image) {
                      imgUrl = addServerPath(`/public/object_images_files/first_image/${item?.object?.id}/?preview=1`)
                    } else if (item?.object?.type?.icon) {
                      imgUrl = addServerPath(`/admin/icon_files/${item?.object?.type?.icon}/picto_black/`)
                    } else if (iconCollectionID) {
                      imgUrl = addServerPath(`/admin/icon_files/${iconCollectionID}/picto_black/`)
                    }
                    break;
                  }
                }
              } catch (err) {
                imgUrl = require('@/assets/svg/location.svg')
              }

              let image = new Image();
              image.crossOrigin = 'Anonymous';
              image.onload = () => {
                if (imgUrl.length) {
                  const tempCanvas = document.createElement('canvas');
                  const tempCtx = tempCanvas.getContext('2d');
                  tempCanvas.width = iconSize;
                  tempCanvas.height = iconSize;

                  tempCtx.beginPath();
                  tempCtx.arc(tempCanvas.width / 2, tempCanvas.height / 2, (tempCanvas.width / 2) - 1, 0, 2 * Math.PI, false);
                  if (item.styles.icon.backgroundColor) {
                    tempCtx.fillStyle = item.styles.icon.backgroundColor;
                    tempCtx.fill();
                  }
                  if (item.styles.icon.borderColor) {
                    tempCtx.lineWidth = 2;
                    tempCtx.strokeStyle = item.styles.icon.borderColor;
                    tempCtx.stroke();
                  }
                  tempCtx.closePath();

                  let imgWidth = image.width;
                  let imgHeight = image.height;
                  let scaleFactor = ((Math.max(tempCanvas.width, tempCanvas.height) / 2) - 1) * 2 / Math.max(imgWidth, imgHeight) / 1.5;
                  let newWidth = imgWidth * scaleFactor;
                  let newHeight = imgHeight * scaleFactor;
                  let xPos = (tempCanvas.width / 2) - newWidth / 2;
                  let yPos = (tempCanvas.height / 2) - newHeight / 2;

                  tempCtx.save();
                  tempCtx.beginPath();
                  tempCtx.arc((tempCanvas.width / 2), (tempCanvas.height / 2), ((tempCanvas.width / 2) - 1), 0, 2 * Math.PI, false);
                  tempCtx.clip();
                  tempCtx.drawImage(image, xPos, yPos, newWidth, newHeight);
                  tempCtx.closePath();
                  tempCtx.restore();

                  if (item.styles.icon.shadow === 'has') {
                    ctx.filter = 'brightness(60%)'
                    ctx.drawImage(tempCanvas, canvasPoint.x + 8, canvasPoint.y + 8, iconSize - 4, iconSize - 4);
                    ctx.filter = 'brightness(100%)'
                  }
                  ctx.drawImage(tempCanvas, canvasPoint.x, canvasPoint.y, iconSize, iconSize);
                } else {
                  ctx.drawImage(image, canvasPoint.x, canvasPoint.y, iconSize, iconSize);
                }
                resolve();
              }

              image.src = imgUrl.length ? imgUrl : require('@/assets/svg/location.svg')
            }
          }
        } else {
          resolve()
        }
      })
    },
    readyMapHandler() {
      this.init()
    },
    hexToRgb: hexToRgb,
    normalizeStrokeStyle(style) {
      if (!style) {
        return style;
      }

      switch (style) {
      case 'dotted':
        return 'dot'
      case 'dashed':
        return 'dash'
      default:
        return style
      }
    },
    applyCanvasLineDash(ctx, style = null) {
      if (!ctx || typeof ctx.setLineDash !== 'function') {
        return
      }

      const normalizedStyle = this.normalizeStrokeStyle(style)

      const dashMap = {
        dot: [2, 6],
        shortdash: [5, 10],
        dash: [10, 15],
        longdash: [15, 20]
      }

      const dash = dashMap[normalizedStyle]

      ctx.setLineDash(dash ? dash : [])
    },
    async getPictoByObj(id, type = 'collection') {
      if (type === 'collection') {
        const response = await this.$store.dispatch('backgrounds/fetchPublicObject', id);

        return new Promise(resolve => {
          this.pictoByObj[id] = response.picto;
          resolve();
        })
      } else {
        const image = BUILD_GENERAL_STORAGE_IMAGE_PATH(id);

        return new Promise(resolve => {
          this.pictoByObj[id] = image;
          resolve();
        })
      }
    },
    resizeHandler: debounce(function() {
      if (window.ymaps) {
        setTimeout(() => {
          if (this.instance) {
            this.instance.destroy()
          }
          this.init()
        }, 100)
      }
    }, 200)
  }
}
